// PollTile.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Services
import { hasUserVotedOnPoll } from 'services/polls/PollReadService';

// CSS
import './PollTile.css';

// Components
import PollResults from '../results/PollResults';
import PollVote from '../vote/PollVote';

/**
 * PollTile
 * 
 * Displays a poll grid item.
 * 
 * @param {object} node - Node containing the poll item widget.
 *  
 *
 * @returns {ReactNode} - PollTile component.
 */
const PollTile = ({ node }) => {

    // ----- CONTEXT ----- //

    // Global Context

    const { user } = useUser();

    // ----- STATE ----- //

    // Local state

    const [hasVoted, setHasVoted] = useState(false);
    const [voteLoaded, setVoteLoaded] = useState(false);

    // ----- EFFECTS ----- //

    /**
     * Check if this user has already voted on this poll node.
     */

    useEffect(() => {
        const checkVote = async () => {
            if (!user?.key || !node?.key) return;

            try {
                const voted = await hasUserVotedOnPoll(node.key, user.key);
                if (voted) setHasVoted(true);
            } catch (err) {
                console.error('Failed to check vote:', err);
            } finally {
                setVoteLoaded(true);
            }
        };

        checkVote();
    }, [node?.key, user?.key]);

    // ----- HANDLERS ----- //

    /**
 * Handles vote.
 *
 * @param {*} index
 */

    const handleVote = (index) => {
        setHasVoted(true); // immediately switch to results view
    };

    // ----- RENDER ----- //

    return (

        <div className="poll-tile-container">

            {/* INNER */}
            <div className="poll-tile-inner">

                {node.pollinfo?.ready && !voteLoaded && (
                    <div className="poll-tile-loading-placeholder">Loading poll...</div>
                )}

                {node.pollinfo?.ready && voteLoaded && hasVoted && (
                    <PollResults
                        node={node}
                    />
                )}

                {node.pollinfo?.ready && voteLoaded && !hasVoted && (
                    <PollVote
                      node={node}
                      onVote={handleVote}
                    />
                )}

            </div>

        </div>

    );
};

// Dev Tools
PollTile.displayName = 'PollTile';

export default PollTile;
